import {
  type CompleteDemoScheduledIntakeRequest,
  CompleteDemoScheduledIntakeRequestSchema,
  type CreateDemoPublicBookingRequest,
  CreateDemoPublicBookingRequestSchema,
  type DemoAvailabilityResponse,
  DemoAvailabilityResponseSchema,
  type DemoPublicBookingReceipt,
  DemoPublicBookingReceiptSchema,
  type DemoPublicSchedulingConfiguration,
  DemoPublicSchedulingConfigurationSchema,
  type DemoScheduledIntakeCompletionReceipt,
  DemoScheduledIntakeCompletionReceiptSchema,
  type DemoScheduledIntakeSession,
  DemoScheduledIntakeSessionSchema,
  DemoScheduledIntakeTokenSchema,
} from "@emr/contracts";
import { answersNotAccepted, appointmentChanged } from "../lib/validation";

export class DemoBookingApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "DemoBookingApiError";
  }
}

function apiOrigin(): string {
  const configured = import.meta.env.VITE_AGENT_API_ORIGIN;
  return typeof configured === "string" && configured.length > 0
    ? configured.replace(/\/$/, "")
    : "http://127.0.0.1:4100";
}

async function responseError(response: Response, fallback: string): Promise<DemoBookingApiError> {
  const body = (await response.json().catch(() => null)) as {
    readonly error?: { readonly code?: unknown; readonly message?: unknown };
  } | null;
  const code = body?.error?.code;
  const message = body?.error?.message;
  return new DemoBookingApiError(
    typeof message === "string" && message.length > 0 ? message : fallback,
    response.status,
    typeof code === "string" ? code : undefined,
  );
}

async function getJson(path: string, signal?: AbortSignal): Promise<unknown> {
  const response = await fetch(`${apiOrigin()}${path}`, {
    method: "GET",
    cache: "no-store",
    ...(signal === undefined ? {} : { signal }),
  });
  if (!response.ok) {
    throw await responseError(response, "The booking service could not be reached");
  }
  return response.json();
}

export const demoSchedulingClient = {
  async configuration(signal?: AbortSignal): Promise<DemoPublicSchedulingConfiguration> {
    return DemoPublicSchedulingConfigurationSchema.parse(
      await getJson("/v1/demo/booking/configuration", signal),
    );
  },

  async availability(
    input: {
      readonly fromDate: string;
      readonly toDate: string;
      readonly appointmentTypeId: string;
      readonly providerId?: string;
    },
    signal?: AbortSignal,
  ): Promise<DemoAvailabilityResponse> {
    const query = new URLSearchParams({
      fromDate: input.fromDate,
      toDate: input.toDate,
      appointmentTypeId: input.appointmentTypeId,
    });
    if (input.providerId !== undefined) query.set("providerId", input.providerId);
    return DemoAvailabilityResponseSchema.parse(
      await getJson(`/v1/demo/booking/availability?${query.toString()}`, signal),
    );
  },

  async createAppointment(
    input: CreateDemoPublicBookingRequest,
    signal?: AbortSignal,
  ): Promise<DemoPublicBookingReceipt> {
    const request = CreateDemoPublicBookingRequestSchema.parse(input);
    const response = await fetch(`${apiOrigin()}/v1/demo/booking/appointments`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(request),
      cache: "no-store",
      ...(signal === undefined ? {} : { signal }),
    });
    if (!response.ok) {
      throw await responseError(
        response,
        response.status === 409
          ? "That appointment time was just taken. Please choose another time."
          : "The appointment could not be booked",
      );
    }
    return DemoPublicBookingReceiptSchema.parse(await response.json());
  },

  async scheduledIntake(token: string, signal?: AbortSignal): Promise<DemoScheduledIntakeSession> {
    const safeToken = DemoScheduledIntakeTokenSchema.parse(token);
    return DemoScheduledIntakeSessionSchema.parse(
      await getJson(`/v1/demo/intake/${encodeURIComponent(safeToken)}`, signal),
    );
  },

  async completeScheduledIntake(
    token: string,
    input: CompleteDemoScheduledIntakeRequest,
    signal?: AbortSignal,
  ): Promise<DemoScheduledIntakeCompletionReceipt> {
    const safeToken = DemoScheduledIntakeTokenSchema.parse(token);
    const request = CompleteDemoScheduledIntakeRequestSchema.parse(input);
    const response = await fetch(
      `${apiOrigin()}/v1/demo/intake/${encodeURIComponent(safeToken)}/complete`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(request),
        cache: "no-store",
        ...(signal === undefined ? {} : { signal }),
      },
    );
    if (!response.ok) {
      // A refusal of the answers themselves gets patient wording, never the service's own text.
      if (response.status === 400 || response.status === 422) {
        throw new DemoBookingApiError(answersNotAccepted, response.status);
      }
      // The appointment changed while the questionnaire was open: retrying the answers cannot help.
      if (response.status === 409) {
        throw new DemoBookingApiError(appointmentChanged, response.status);
      }
      throw await responseError(response, "The questionnaire could not be submitted");
    }
    return DemoScheduledIntakeCompletionReceiptSchema.parse(await response.json());
  },
};
